import React, { useState } from "react";
import { PenLine, Loader2, Copy, Check, Send, Globe, Mail, Share2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ImagePicker from "@/components/admin/ImagePicker";

const TONES = ["warm & friendly", "professional", "excited / punchy", "informative & calm"];
const CHANNELS = [
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "twitter", label: "X (Twitter)" },
];

const CopyBtn = ({ text, testid }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error("Copy blocked by browser.");
    }
  };
  return (
    <button
      onClick={copy}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-widest border border-white/15 hover:border-cyan-400/50 text-slate-300 rounded-sm"
    >
      {done ? <Check size={13} className="text-cyan-300" /> : <Copy size={13} />}
      {done ? "Copied" : "Copy"}
    </button>
  );
};

const WriteOncePanel = () => {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [tone, setTone] = useState(TONES[0]);
  const [channels, setChannels] = useState(["facebook", "instagram", "linkedin"]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const [imageUrl, setImageUrl] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [pubChannels, setPubChannels] = useState([]);

  const toggle = (id) =>
    setChannels((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const generate = async () => {
    if (!text.trim()) return toast.error("Write a few lines first.");
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(
        "/writeonce/generate",
        { title, text, tone, channels },
        { timeout: 120000 }
      );
      setResult(data);
      setPubChannels(Object.keys(data.social || {}));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed.");
    } finally {
      setBusy(false);
    }
  };

  const updateSocial = (ch, val) =>
    setResult((r) => ({ ...r, social: { ...r.social, [ch]: val } }));

  const togglePub = (id) =>
    setPubChannels((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const publish = async () => {
    const chosen = pubChannels.filter((c) => (result.social[c] || "").trim());
    if (!chosen.length) return toast.error("Pick at least one channel with content.");
    const schedule_date = scheduleAt ? new Date(scheduleAt).toISOString() : null;
    if (!schedule_date && !window.confirm(`Publish now to ${chosen.join(", ")}? This posts to your real connected accounts.`)) return;
    setPublishing(true);

    // Scheduled → drop onto the Calendar queue (auto-publishes at the set time,
    // all channels incl. Instagram). No time → publish now.
    if (schedule_date) {
      let queued = 0;
      for (const ch of chosen) {
        try {
          await api.post("/writeonce/queue", {
            caption: result.social[ch],
            channels: [ch],
            image_url: imageUrl,
            title: title || result.social[ch].slice(0, 40),
            starts_at: schedule_date,
          });
          queued += 1;
        } catch (e) {
          toast.error(`${ch}: ${e?.response?.data?.detail || "could not queue"}`);
        }
      }
      if (queued)
        toast.success(`Queued ${queued} post${queued > 1 ? "s" : ""} to your Calendar for ${new Date(scheduleAt).toLocaleString()}.`);
      setPublishing(false);
      return;
    }

    let okCount = 0;
    for (const ch of chosen) {
      try {
        const { data } = await api.post("/writeonce/publish", {
          caption: result.social[ch],
          channels: [ch],
          image_url: imageUrl,
          schedule_date: null,
        });
        const r = (data.results || {})[ch] || {};
        if (r.status === "published" || r.status === "scheduled") okCount += 1;
        else toast.error(`${ch}: ${r.error || r.status || "failed"}`);
      } catch (e) {
        toast.error(`${ch}: ${e?.response?.data?.detail || "failed"}`);
      }
    }
    if (okCount) toast.success(`Published to ${okCount} channel${okCount > 1 ? "s" : ""}.`);
    setPublishing(false);
  };

  return (
    <div className="space-y-6" data-testid="writeonce-panel">
      <div className="bg-[#0A192F] border border-white/10 rounded-sm p-6">
        <div className="flex items-center gap-2 text-cyan-300 font-mono text-[10px] uppercase tracking-[0.3em] mb-3">
          <PenLine size={14} /> Write Once
        </div>
        <h3 className="font-heading text-2xl font-bold text-white">Write it once. Get every version.</h3>
        <p className="mt-1 text-slate-400 text-sm">
          Type it once — get a social post, a website article and a newsletter version. No re-typing.
        </p>

        <div className="mt-5 space-y-3">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (e.g. Paisley Food Festival returns this weekend)"
            data-testid="writeonce-title"
            className="w-full px-4 py-3 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white placeholder-slate-500 text-sm rounded-sm"
          />
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            placeholder="Write your rough content here — a few lines is plenty."
            data-testid="writeonce-text"
            className="w-full px-4 py-3 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white placeholder-slate-500 text-sm rounded-sm resize-none"
          />
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              data-testid="writeonce-tone"
              className="px-3 py-2 bg-[#050B14] border border-white/10 text-white text-sm rounded-sm"
            >
              {TONES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {CHANNELS.map((c) => (
              <button
                key={c.id}
                onClick={() => toggle(c.id)}
                data-testid={`writeonce-channel-${c.id}`}
                className={[
                  "px-3 py-2 text-xs font-mono uppercase tracking-widest rounded-sm border",
                  channels.includes(c.id)
                    ? "bg-cyan-400/10 text-cyan-300 border-cyan-400/40"
                    : "text-slate-400 border-white/10 hover:border-white/30",
                ].join(" ")}
              >
                {c.label}
              </button>
            ))}
            <button
              onClick={generate}
              disabled={busy}
              data-testid="writeonce-generate"
              className="ml-auto px-5 py-2.5 bg-[#00F0FF] text-[#050B14] font-bold text-sm rounded-sm hover:bg-[#33F3FF] disabled:opacity-60 flex items-center gap-2"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <PenLine size={16} />}
              Generate versions
            </button>
          </div>
          {busy && (
            <p className="text-[11px] font-mono text-cyan-300/80">Writing your versions… a few seconds.</p>
          )}
        </div>
      </div>

      {result && (
        <>
          {/* SOCIAL */}
          <div className="bg-[#0A192F] border border-white/10 rounded-sm p-5" data-testid="writeonce-social">
            <div className="flex items-center gap-2 mb-3">
              <Share2 size={16} className="text-cyan-300" />
              <h4 className="text-white font-bold">Social posts</h4>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {Object.entries(result.social).map(([ch, val]) => (
                <div key={ch} className="bg-[#050B14] border border-white/10 rounded-sm p-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-300">
                      <input
                        type="checkbox"
                        checked={pubChannels.includes(ch)}
                        onChange={() => togglePub(ch)}
                        data-testid={`writeonce-pub-${ch}`}
                      />
                      {ch}
                    </label>
                    <CopyBtn text={val} testid={`writeonce-copy-${ch}`} />
                  </div>
                  <textarea
                    value={val}
                    onChange={(e) => updateSocial(ch, e.target.value)}
                    rows={5}
                    data-testid={`writeonce-social-${ch}`}
                    className="w-full px-3 py-2 bg-transparent border border-white/10 focus:border-cyan-400 outline-none text-slate-200 text-sm rounded-sm resize-none"
                  />
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                onClick={() => setPickerOpen(true)}
                data-testid="writeonce-add-image"
                className="px-3 py-2 text-xs font-mono uppercase tracking-widest border border-white/15 hover:border-cyan-400/50 text-slate-300 rounded-sm flex items-center gap-2"
              >
                <ImageIcon size={13} /> {imageUrl ? "Change image" : "Add image"}
              </button>
              {imageUrl && (
                <img src={imageUrl} alt="chosen" className="w-10 h-10 object-cover rounded-sm border border-white/15" />
              )}
              <input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                data-testid="writeonce-schedule"
                className="px-3 py-2 bg-[#050B14] border border-white/10 text-white text-sm rounded-sm"
              />
              <button
                onClick={publish}
                disabled={publishing}
                data-testid="writeonce-publish"
                className="ml-auto px-5 py-2.5 bg-[#00F0FF] text-[#050B14] font-bold text-sm rounded-sm hover:bg-[#33F3FF] disabled:opacity-60 flex items-center gap-2"
              >
                {publishing ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {scheduleAt ? "Queue to Calendar" : "Publish now"}
              </button>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">
              Set a time to queue it on your Calendar (drips out automatically, all channels). Leave the time blank to post now. Facebook & Instagram post directly; LinkedIn & X need to be linked.
            </p>
          </div>

          {/* WEBSITE */}
          <div className="bg-[#0A192F] border border-white/10 rounded-sm p-5" data-testid="writeonce-website">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Globe size={16} className="text-cyan-300" />
                <h4 className="text-white font-bold">Website article</h4>
              </div>
              <CopyBtn text={result.website} testid="writeonce-copy-website" />
            </div>
            <textarea
              value={result.website}
              onChange={(e) => setResult((r) => ({ ...r, website: e.target.value }))}
              rows={10}
              data-testid="writeonce-website-text"
              className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-slate-200 text-sm rounded-sm resize-none"
            />
            <p className="mt-2 text-[11px] text-slate-500">Copy this into your website editor.</p>
          </div>

          {/* NEWSLETTER */}
          <div className="bg-[#0A192F] border border-white/10 rounded-sm p-5" data-testid="writeonce-newsletter">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Mail size={16} className="text-cyan-300" />
                <h4 className="text-white font-bold">Newsletter version</h4>
              </div>
              <CopyBtn text={result.newsletter} testid="writeonce-copy-newsletter" />
            </div>
            <textarea
              value={result.newsletter}
              onChange={(e) => setResult((r) => ({ ...r, newsletter: e.target.value }))}
              rows={7}
              data-testid="writeonce-newsletter-text"
              className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-slate-200 text-sm rounded-sm resize-none"
            />
          </div>
        </>
      )}

      {pickerOpen && (
        <ImagePicker
          onClose={() => setPickerOpen(false)}
          onPick={(url) => {
            setImageUrl(url);
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default WriteOncePanel;
